import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";

// Icons
import { RxDashboard } from "react-icons/rx";
import { FaAngleRight, FaAngleDown } from "react-icons/fa";
import { IoCreateOutline, IoDocumentsOutline } from "react-icons/io5";
import { CiCircleAlert, CiBank, CiSettings } from "react-icons/ci";
import { LuWarehouse } from "react-icons/lu";
import { MdLocalOffer } from "react-icons/md";
import { TbReport } from "react-icons/tb";
import { GoMilestone } from "react-icons/go";
import { MdSupportAgent } from "react-icons/md";
import { RiGroupLine } from "react-icons/ri";
import { CgProfile } from "react-icons/cg";
import { GiBrain } from "react-icons/gi";
import { MdIntegrationInstructions } from "react-icons/md";
import { LogOut } from "lucide-react";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useMyContext } from "../../../context/MyContext";
import "../../css/custom-scrollbar.css";
import { useAuth } from "../../../routes/Auth";

const Sidebar = () => {
  const { profile_flag, isToggleSidebar, setIsToggleSidebar, isDarkMode } =
    useMyContext();

  const [activeTab, setActiveTab] = useState(-1);
  const [activeSubTab, setActiveSubTab] = useState(-1);

  // Collapsed-rail tooltip: rendered through a portal (see bottom of
  // this component) so it is never clipped by the sidebar's own
  // `overflow-hidden`/`overflow-y-auto` containers or by the
  // `overflow-hidden` wrapper around <Sidebar /> in the dashboard page.
  const [hoverTooltip, setHoverTooltip] = useState(null); // { text, top, left } | null

  const showTooltip = useCallback(
    (event, text) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoverTooltip({
        text,
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    },
    []
  );

  const hideTooltip = useCallback(() => setHoverTooltip(null), []);

  const navigate = useNavigate();
  const { logout } = useAuth();

  const sidebarRef = useRef(null);
  const userRole = localStorage.getItem("role");

  // ============================================================
  // MENU ITEMS
  // ============================================================

  let menuItems = [
    { title: "Dashboard", icon: <RxDashboard />, href: "/dashboard", submenu: [],color: "#2563eb" },
    {
      title: "Shipments",  icon: <IoCreateOutline />,  href: "/shipments/all" , submenu: [], color: "#9333ea",
    },
    {
      title: "Warehouse",   icon: <LuWarehouse />, href: "/warehouse/all" , submenu: [], color: "#059669",
    },
    { title: "Integration", icon: <MdIntegrationInstructions />, href: "/intregration", submenu: [],color: "#d97706", },
    {
      title: "Disputes",
      icon: <CiCircleAlert />,
      submenu: [
        { title: "NDR", href: "/disputes/NDR" },
        { title: "Weight Disputes", href: "/disputes/weight" },
        { title: "Weight Upload", href: "/disputes/wightUpload" },
        { title: "All Weight Disputes", href: "/disputes/allWeightDisputes" },
      ],
      color: "#dc2626",
    },
    {
      title: "Payments",
      icon: <CiBank />,
      submenu: [
        { title: "Remmitance", href: "/payments/remmitance" },
        { title: "RemmitanceList", href: "/payments/remmitanceList" },
        { title: "Wallet Balance", href: "/payments/walletbalance" },
      ],
       color: "#0891b2",
    },
    { title: "Label", icon: <IoDocumentsOutline />, href: "/labelCustomized", submenu: [], color: "#7c3aed" },
    { title: "Services", icon: <GoMilestone />, href: "/services", submenu: [],color: "#db2777" },
    {
      title: "Offers",
      icon: <MdLocalOffer />,
      submenu: [
        { title: "Set Offer", href: "/offer/setoffer" },
        { title: "All offers", href: "/offer/viewAllOffers" },
      ],
      color: "#ea580c" 
    },
    {
      title: "Customers",
      icon: <RiGroupLine />,
      submenu: [
        { title: "ViewAllCustomer", href: "/customers/viewCustomer" },
        { title: "KYC Pending", href: "/customers/kycPending" },
      ],
      color: "#16a34a",
    },
    {
      title: "Preship",
      icon: <GiBrain />,
      submenu: [
        { title: "Rate Calculator", href: "/settings/rateCalculator" },
        { title: "Servicable Pincodes", href: "/settings/pincode" },
        { title: "Rate Card", href: "/settings/rateCard" },
        { title: "Add Pincodes", href: "/settings/addPincode" },
        { title: "Set Rate", href: "/settings/setRate" },
      ],
      color: "#4f46e5",
    },
    { title: "Reports", icon: <TbReport />, href: "/reports", submenu: [],color: "#0d9488"  },
    { title: "Help", icon: <MdSupportAgent />, href: "/help", submenu: [],color: "#65a30d" },
    {
      title: "Setting",
      icon: <CiSettings />,
      submenu: [
        { title: "Add Employee", href: "/addEmployee" },
        { title: "All Employee", href: "/allEmployee" },
      ],
      color: "#525252",
    },
    { title: "Profile", icon: <CgProfile />, href: "/profile", submenu: [],color: "#0284c7"  },
  ];

  // ============================================================
  // ROLE BASED MENU
  // ============================================================

  if (userRole !== "admin") {
    menuItems.forEach((item) => {
      if (item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) =>
            subItem.title !== "Weight Upload" &&
            subItem.title !== "All Weight Disputes"
        );
      }
      if (item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Set Rate" && subItem.title !== "Add Pincodes"
        );
      }
      if (item.title === "Payments") {
        item.submenu = item.submenu.filter((subItem) => subItem.title !== "RemmitanceList");
      }
    });

    menuItems = menuItems.filter(
      (item) => item.title !== "Customers" && item.title !== "Offers" && item.title !== "Setting"
    );
  } else {
    menuItems.forEach((item) => {
      if (item.title === "Preship") {
        item.submenu = item.submenu.filter((subItem) => subItem.title !== "Rate Card");
      }
      if (item.title === "Disputes") {
        item.submenu = item.submenu.filter((subItem) => subItem.title !== "Weight Disputes");
      }
      if (item.title === "Payments") {
        item.submenu = item.submenu.filter((subItem) => subItem.title !== "Remmitance");
      }
    });

    menuItems = menuItems.filter((item) => item.title !== "Label");
  }

  // ============================================================
  // ACTIVE ROUTE
  // ============================================================

  useEffect(() => {
    const currentRoute = window.location.pathname;

    menuItems.forEach((item, index) => {
      if (item.href === currentRoute) {
        setActiveTab(index);
        setActiveSubTab(-1);
      }

      if (item.submenu?.length > 0) {
        item.submenu.forEach((subItem, subIndex) => {
          if (subItem.href === currentRoute) {
            setActiveTab(index);
            setActiveSubTab(subIndex);
          }
        });
      }
    });
  }, []);

  // ============================================================
  // MENU TOGGLE
  // ============================================================

  const toggleSubmenu = useCallback(
    (index, href) => {
      if (profile_flag === "0" && userRole !== "admin") {
        toast.error("Please Contact to the Support Team, Your KYC is Pending");
        return;
      }

      if (href) {
        navigate(href);
        return;
      }

      // If the sidebar is collapsed (icon-only rail), expand it first
      // so the submenu has room to be shown.
      if (!isToggleSidebar) {
        setIsToggleSidebar(true);
      }

      setActiveTab((prev) => (prev === index ? -1 : index));
      setActiveSubTab(-1);
    },
    [profile_flag, userRole, navigate, isToggleSidebar, setIsToggleSidebar]
  );

  // ============================================================
  // MOBILE OUTSIDE CLICK
  // ============================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        window.innerWidth <= 768
      ) {
        setIsToggleSidebar(false);
      }
    };

    if (isToggleSidebar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isToggleSidebar, setIsToggleSidebar]);

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("cus_id");
    localStorage.removeItem("token");
    localStorage.removeItem("wallet_status");
    localStorage.removeItem("profile_flag");
    localStorage.removeItem("role");
    localStorage.removeItem("isB2BMode");

    navigate("/login");
    logout();
    toast.success("Logout Successfully!");
  };

  // ============================================================
  // MUI sx CAN'T READ TAILWIND `dark:` CLASSES,
  // SO WE BRANCH MANUALLY HERE USING isDarkMode
  // ============================================================

  const activeBg = isDarkMode
    ? "linear-gradient(90deg, rgba(255,255,255,0.10), rgba(255,255,255,0.06), rgba(255,255,255,0.03))"
    : "linear-gradient(90deg, rgba(255,255,255,0.95), rgba(243,244,246,0.8), rgba(229,231,235,0.65))";

  const activeBorder = isDarkMode
    ? "1px solid rgba(255,255,255,0.08)"
    : "1px solid rgba(0,0,0,0.04)";

  const activeShadow = isDarkMode
    ? "0 1px 2px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.3), 0 10px 24px rgba(0,0,0,0.25)"
    : "0 1px 2px rgba(16,24,40,0.04), 0 4px 10px rgba(16,24,40,0.06), 0 10px 24px rgba(16,24,40,0.05)";

  const activeTextColor = isDarkMode ? "#f1f5f9" : "#1f2937";
  const defaultTextColor = isDarkMode ? "rgba(203,213,225,0.75)" : "rgba(75,85,99,0.75)";

  // Whether the sidebar is expanded (full, with text) or collapsed
  // (narrow icon-only rail). isToggleSidebar === true -> expanded.
  const isExpanded = isToggleSidebar;

  // ============================================================
  // SIDEBAR
  // ============================================================

  return (
    <>
    <aside
      ref={sidebarRef}
      className={`
        relative
        ${isExpanded ? "w-64" : "w-[72px]"}
        h-full
        flex
        flex-col
        overflow-hidden
        text-gray-700
        dark:text-gray-200
        sidebar-glass
        transition-all
        duration-300
        ease-in-out
      `}
    >
      {/* =====================================================
          BACKGROUND
      ===================================================== */}

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-white dark:bg-[#12162a]" />

        <div
          className="
            absolute
            top-0
            left-0
            right-0
            h-24
            bg-gradient-to-b
            from-gray-50/80
            via-white/40
            to-transparent
            dark:from-white/[0.03]
            dark:via-white/[0.0]
            dark:to-transparent
          "
        />

        <div
          className="
            absolute
            top-0
            right-0
            bottom-0
            w-px
            bg-gray-200/70
            dark:bg-white/[0.06]
          "
        />

        <div
          className="
            absolute
            bottom-0
            left-0
            right-0
            h-16
            bg-gradient-to-t
            from-gray-900/[0.02]
            to-transparent
            dark:from-black/25
            dark:to-transparent
          "
        />
      </div>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <div className="relative z-10 flex flex-col h-full">

        {/* =================================================
            MENU CONTAINER
        ================================================= */}

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto px-3 pt-4">
            <ul className="">
              {menuItems.map((item, index) => {
                const isActive = activeTab === index;

                return (
                  <li
                    key={index}
                    className="group relative"
                    onMouseEnter={(e) => {
                      if (!isExpanded) showTooltip(e, item.title);
                    }}
                    onMouseLeave={() => {
                      if (!isExpanded) hideTooltip();
                    }}
                  >
                    {/* =================================================
                        MAIN MENU BUTTON
                    ================================================= */}

                    <Button
                      onClick={() => toggleSubmenu(index, item.href)}
                      sx={{
                        minWidth: 0,
                        fontFamily: "var(--font-custom-open-sans)",
                        textTransform: "none",
                        width: "100%",
                        textAlign: "left",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isExpanded ? "flex-start" : "center",
                        gap: isExpanded ? "10px" : "0px",
                        padding: isExpanded ? "9px 12px" : "9px 0",
                        borderRadius: "16px",
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",

                        color: isActive ? activeTextColor : defaultTextColor,
                        background: isActive ? activeBg : "transparent",
                        border: isActive ? activeBorder : "1px solid transparent",
                        boxShadow: isActive ? activeShadow : "none",

                        "&:hover": {
                          background: activeBg,
                          border: activeBorder,
                          color: activeTextColor,
                          boxShadow: activeShadow,
                        },
                      }}
                    >
                      {/* =================================================
                          ICON — dark rounded badge
                      ================================================= */}
<span
  className={`
    relative
    z-10
    flex
    items-center
    justify-center
    w-7
    h-7
    rounded-full
    text-[14px]
    shrink-0
    transition-all
    duration-300
    ${isActive
      ? "shadow-lg dark:bg-slate-100 dark:text-gray-900 group-hover:text-white dark:group-hover:bg-slate-100"
      : "bg-transparent text-gray-400 dark:text-gray-500 group-hover:bg-gray-900 group-hover:text-white dark:group-hover:bg-slate-100 dark:group-hover:text-gray-900"
    }
  `}
  style={
    isActive
      ? { color: item.color }
      : {}
  }
  onMouseEnter={(e) => {
    if (!isActive) e.currentTarget.style.backgroundColor = item.color;
  }}
  onMouseLeave={(e) => {
    if (!isActive) e.currentTarget.style.backgroundColor = "";
  }}
>
  {item.icon}
</span>
                      {/* =================================================
                          TITLE (hidden when collapsed)
                      ================================================= */}

                      {isExpanded && (
                        <span
                          className={`
                            relative
                            z-10
                            flex-1
                            text-left
                            text-[12px]
                            font-medium
                            transition-colors
                            duration-300
                            ${isActive
                              ? "text-gray-800 dark:text-slate-100"
                              : "text-gray-600 dark:text-slate-400 group-hover:text-gray-800 dark:group-hover:text-slate-100"
                            }
                          `}
                        >
                          {item.title}
                        </span>
                      )}

                      {/* =================================================
                          ARROW (hidden when collapsed)
                      ================================================= */}

                      {isExpanded && item.submenu.length > 0 && (
                        <span
                          className={`
                            relative
                            z-10
                            text-[9px]
                            mr-1
                            transition-all
                            duration-300
                            ${isActive
                              ? "text-gray-500 dark:text-slate-400 rotate-180"
                              : "text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-300"
                            }
                          `}
                        >
                          {isActive ? <FaAngleDown /> : <FaAngleRight />}
                        </span>
                      )}
                    </Button>

                    {/* =================================================
                        SUBMENU (expanded rail only)
                    ================================================= */}

                    {item.submenu.length > 0 && isActive && isExpanded && (
                      <div className="relative ml-5 mt-1 mb-1">
                        <ul className="relative z-10 space-y-0.5 py-1 px-0.5">
                          {item.submenu.map((subItem, subIndex) => {
                            const isSubActive = activeSubTab === subIndex;

                            return (
                              <li key={subIndex}>
                                <Link
                                  to={subItem.href}
                                  onClick={() => setActiveSubTab(subIndex)}
                                  className={`
                                    flex
                                    items-center
                                    px-3
                                    py-2
                                    rounded-full
                                    text-[10px]
                                    font-medium
                                    transition-all
                                    duration-300
                                    border
                                    ${isSubActive
                                      ? "text-gray-800 bg-white border-black/[0.04] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06)] dark:text-slate-100 dark:bg-white/[0.08] dark:border-white/[0.08]"
                                      : "text-gray-500 border-transparent hover:text-gray-800 hover:bg-white hover:border-black/[0.04] hover:shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06)] dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-white/[0.06] dark:hover:border-white/[0.08]"
                                    }
                                  `}
                                >
                                  {isSubActive && (
                                    <span className="w-1 h-1 rounded-full bg-gray-900 dark:bg-slate-100 mr-2" />
                                  )}
                                  <span>{subItem.title}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* =================================================
            LOGOUT
        ================================================= */}

        <div className="px-3 pb-3 pt-2">
          <div className="h-px bg-gray-200/70 dark:bg-white/[0.08] mb-3" />

          <div
            className="relative group"
            onMouseEnter={(e) => {
              if (!isExpanded) showTooltip(e, "Logout");
            }}
            onMouseLeave={() => {
              if (!isExpanded) hideTooltip();
            }}
          >
            <button
              onClick={handleLogout}
              className="
                group
                relative
                w-full
                flex
                items-center
                justify-center
                gap-2.5
                px-3
                py-2
                rounded-2xl
                text-left
                text-[11px]
                font-medium
                text-white
                bg-red-600
                border
                border-transparent
                transition-all
                duration-300
                hover:bg-gray-300
                hover:border-black/[0.04]
                hover:shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06),0_10px_24px_rgba(16,24,40,0.05)]
                hover:text-gray-600
                active:scale-[0.98]
              "
            >
              <div
                className="
                  flex
                  items-center
                  justify-center
                  w-7
                  h-7
                  rounded-full
                  border
                  border-gray-100/70
                  dark:border-white/20
                  text-white
                  transition-all
                  duration-300
                  group-hover:bg-gray-900
                  group-hover:text-white
                  group-hover:border-transparent
                "
              >
                <LogOut color="white" size={13} />
              </div>

              {isExpanded && <span className="text-[14px]">Logout</span>}
            </button>
          </div>
        </div>
      </div>
    </aside>

    {/* =====================================================
        COLLAPSED-RAIL TOOLTIP — rendered via portal straight
        into document.body so no ancestor's overflow can clip it.
    ===================================================== */}
    {!isExpanded &&
      hoverTooltip &&
      createPortal(
        <span
          style={{
            position: "fixed",
            top: hoverTooltip.top,
            left: hoverTooltip.left,
            transform: "translateY(-50%)",
          }}
          className="
            pointer-events-none
            whitespace-nowrap
            rounded-md
            bg-gray-800
            dark:bg-indigo-950
            dark:border
            dark:border-indigo-500/40
            px-2.5
            py-1
            text-[11px]
            text-white
            dark:text-indigo-100
            shadow-lg
            z-[9999]
          "
        >
          {hoverTooltip.text}
        </span>,
        document.body
      )}
    </>
  );
};

export default Sidebar;